import { Flex } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Text } from '@shared/ui/antd';

import type { UiMessage } from '../lib/types';
import { AgentTestMessageItem } from './agent-test-message-item';
import styles from '../agent-test-chat.module.scss';

interface AgentTestMessagesProps {
  messages: UiMessage[];
  forceScrollSignal?: number;
  isLoading: boolean;
  isFetching: boolean;
  emptyTitle: string;
  emptyDescription: string;
  agentLabel: string;
  agentNamesById?: Record<number, string>;
  switchNoticePrefix: string;
  switchNoticeTooltip: string;
}

export const AgentTestMessages: FC<AgentTestMessagesProps> = ({
  messages,
  forceScrollSignal,
  isLoading,
  isFetching,
  emptyTitle,
  emptyDescription,
  agentLabel,
  agentNamesById,
  switchNoticePrefix,
  switchNoticeTooltip,
}) => {
  const shouldStickToBottomRef = useRef(true);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const isForceScrollingRef = useRef(false);
  const forceScrollTimeoutRef = useRef<number | null>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = messagesRef.current;
    if (!container) return;
    if (!shouldStickToBottomRef.current) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  const forceScrollToBottom = useCallback(() => {
    const container = messagesRef.current;
    if (!container) return;

    if (forceScrollTimeoutRef.current) {
      window.clearTimeout(forceScrollTimeoutRef.current);
      forceScrollTimeoutRef.current = null;
    }

    shouldStickToBottomRef.current = true;
    isForceScrollingRef.current = true;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });

    // Ensure we end exactly at bottom even if content height changed during smooth scroll.
    forceScrollTimeoutRef.current = window.setTimeout(() => {
      const nextContainer = messagesRef.current;
      if (!nextContainer) return;
      shouldStickToBottomRef.current = true;
      isForceScrollingRef.current = false;
      nextContainer.scrollTo({ top: nextContainer.scrollHeight, behavior: 'auto' });
      forceScrollTimeoutRef.current = null;
    }, 350);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (forceScrollSignal === undefined) return;
    forceScrollToBottom();
  }, [forceScrollSignal, forceScrollToBottom]);

  const handleScroll = () => {
    if (isForceScrollingRef.current) return;
    const container = messagesRef.current;
    if (!container) return;
    const threshold = 80;
    shouldStickToBottomRef.current =
      container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
  };

  useEffect(
    () => () => {
      if (!forceScrollTimeoutRef.current) return;
      window.clearTimeout(forceScrollTimeoutRef.current);
      forceScrollTimeoutRef.current = null;
    },
    [],
  );

  return (
    <div className={styles.messages} ref={messagesRef} onScroll={handleScroll}>
      {!messages.length && !isLoading && !isFetching ? (
        <Flex vertical className={styles.empty} gap={4} align="center" justify="center">
          <Text variant="md" fontWeight={600}>
            {emptyTitle}
          </Text>
          <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {emptyDescription}
          </Text>
        </Flex>
      ) : (
        <div className={styles.messagesList}>
          {messages.map((message) => (
            <AgentTestMessageItem
              key={message.id}
              message={message}
              switchNoticeText={
                typeof message.id === 'number' && typeof message.switched_agent_id === 'number'
                  ? `${switchNoticePrefix} ${
                      agentNamesById?.[message.switched_agent_id] ?? agentLabel
                    }`
                  : null
              }
              switchNoticeTooltip={switchNoticeTooltip}
            />
          ))}
        </div>
      )}
    </div>
  );
};
